import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { getPublicProject, activateProject } from '../../lib/publicApi';
import { getCanvasDefinition } from '../../canvases/registry';
import CanvasStage from '../../canvases/CanvasStage';
import './public.css';

const POLL_MS = 5000;

function CenterCard({ theme, icon, title, children }) {
  return (
    <div className={`pub-center-shell theme-${theme}`}>
      <div className="pub-card">
        <div className="pub-icon">{icon}</div>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function CampaignPage() {
  const { domain } = useParams();
  const [state, setState] = useState({ loading: true, error: '', project: null, messages: [] });
  const [code, setCode] = useState('');
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState('');
  const [qrVisible, setQrVisible] = useState(true);
  const qrInitialized = useRef(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await getPublicProject(domain);
      setState({ loading: false, error: '', project: data.project, messages: data.messages });
      if (!qrInitialized.current) {
        setQrVisible(data.project.is_qr_visible !== false);
        qrInitialized.current = true;
      }
    } catch (err) {
      setState({ loading: false, error: err.message, project: null, messages: [] });
    }
  }, [domain]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const isLive = state.project?.status === 'active' && state.project?.is_activated;
    if (!isLive) return undefined;
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [state.project?.status, state.project?.is_activated, load]);

  const canvasTypeKey = state.project?.canvas_library?.moving_object_type || state.project?.canvas_type;
  const canvasDef = useMemo(
    () => (canvasTypeKey ? getCanvasDefinition(canvasTypeKey) : null),
    [canvasTypeKey]
  );
  const theme = canvasDef?.isNightTheme ? 'night' : 'day';

  const handleActivate = async (e) => {
    e.preventDefault();
    setActivateError('');
    setActivating(true);
    try {
      await activateProject(domain, code);
      await load();
    } catch (err) {
      setActivateError(err.message);
    } finally {
      setActivating(false);
    }
  };

  if (state.loading) {
    return <CenterCard theme="night" icon="✨" title="Loading…" />;
  }

  if (state.error || !state.project) {
    return (
      <CenterCard theme="night" icon="🔍" title="Canvas service not available">
        <p>This link doesn't match to an active service.</p>
      </CenterCard>
    );
  }

  const { project } = state;

  if (project.status === 'pending') {
    return (
      <CenterCard theme={theme} icon="⏳" title="Coming soon">
        <p>This campaign hasn't started yet. Please check back on {new Date(project.start_date).toLocaleString()}.</p>
      </CenterCard>
    );
  }

  if (project.status === 'expired') {
    return (
      <CenterCard theme={theme} icon="🌙" title="This campaign has ended">
        <p>Thank you to everyone who took part — the final canvas has been sent to the organiser.</p>
      </CenterCard>
    );
  }

  if (!project.is_activated) {
    return (
      <CenterCard theme={theme} icon="🔑" title="Activate this campaign">
        <p>Enter the 9-character activation code you received to bring the canvas to life.</p>
        {activateError && <div className="pub-alert">{activateError}</div>}
        <form onSubmit={handleActivate}>
          <input
            className="pub-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={9}
            placeholder="ABCD2FGH3"
            required
          />
          <button type="submit" className="pub-btn" disabled={activating || code.trim().length < 9}>
            {activating ? 'Activating…' : 'Activate'}
          </button>
        </form>
      </CenterCard>
    );
  }

  if (!canvasDef) {
    return (
      <CenterCard theme={theme} icon="⚠️" title="Canvas unavailable">
        <p>This campaign uses a canvas template that isn't available right now.</p>
      </CenterCard>
    );
  }

  const submitUrl = `${window.location.origin}/${domain}/submit`;
  const isTouchScreen = typeof window !== 'undefined'
    && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  const openSubmitPage = () => {
    window.location.href = submitUrl;
  };

  return (
    <div className={`pub-canvas-shell theme-${theme}`}>
      <CanvasStage
        createStage={canvasDef.createStage}
        messages={state.messages}
        backgroundImageUrl={project.canvas_library?.background_image_url}
      />

      {qrVisible && (
        <div
          className="pub-qr-card pub-qr-card-touch"
          role="link"
          tabIndex={0}
          onClick={openSubmitPage}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openSubmitPage(); }}
        >
          <QRCodeSVG
            value={submitUrl}
            size={128}
            level="H"
            includeMargin
            fgColor={theme === 'night' ? '#ffffff' : '#111111'}
            bgColor="transparent"
          />
          <span className="pub-qr-hint">Scan or click to leave a message</span>
          <button type="button" className="pub-qr-toggle" onClick={(e) => { e.stopPropagation(); setQrVisible(false); }}>Hide QR</button>
        </div>
      )}
      {!qrVisible && (
        <button
          className="pub-qr-toggle pub-qr-show"
          onClick={() => setQrVisible(true)}
        >
          Show QR
        </button>
      )}
    </div>
  );
}
